import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { Home, Calendar, User, Bell, Plane, Dumbbell, Users, ChevronDown, LogOut } from 'lucide-react';
import { UserContext } from './App';
import { supabase } from './utils/supabaseClient';
import SidebarDrawer from './components/common/SidebarDrawer';
import { SECTION_THEME } from './utils/sectionTheme';
import { useIsDesktop } from './hooks/useIsDesktop';
import { getLevelInfo } from './utils/levels';
import { AvatarDisplay } from './utils/avatars';
import { isMemberRoute, TOP_USER_MENU_SECTIONS } from './utils/memberNavigation';
import { useModal } from './context/ModalContext';
import { MobileDrawerContext } from './context/MobileDrawerContext';
import { useMobileHeader } from './context/MobileHeaderContext';
import LoginPromptModal from './components/common/LoginPromptModal';

const colors = {
    primary: '#4e8bca',
    primaryLight: '#6a9fd3',
    gray: '#64748b',
    white: '#ffffff',
    background: '#f4f7fa'
};

export default function Layout() {
    const location = useLocation();
    const navigate = useNavigate();
    const { currentUser, setSession, setCurrentUser } = useContext(UserContext);
    const { headerLeftAction } = useMobileHeader();
    const { showConfirm } = useModal();
    const [unreadCount, setUnreadCount] = useState(0);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [drawerMode, setDrawerMode] = useState('navigation');
    const [contextualDrawer, setContextualDrawer] = useState(null);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [isLoginPromptOpen, setIsLoginPromptOpen] = useState(false);
    const drawerTriggerRef = useRef(null);
    const isDesktop = useIsDesktop();

    // Phase 1.96: 未完成初始設定時隱藏導覽動線，避免點了又被 OnboardingGate 彈回
    const needsOnboarding = currentUser?.profile?.is_onboarded === false;

    useEffect(() => {
        if (!currentUser) return;
        const fetchUnread = async () => {
            const { count } = await supabase
                .from('notifications')
                .select('*', { count: 'exact', head: true })
                .eq('user_id', currentUser.id)
                .eq('is_read', false);
            setUnreadCount(count || 0);
        };
        fetchUnread();

        // Real-time subscription for new notifications
        const channel = supabase
            .channel('notifications-badge')
            .on('postgres_changes', {
                event: 'INSERT',
                schema: 'public',
                table: 'notifications',
                filter: `user_id=eq.${currentUser.id}`
            }, () => {
                setUnreadCount(prev => prev + 1);
            })
            .on('postgres_changes', {
                event: 'UPDATE',
                schema: 'public',
                table: 'notifications',
                filter: `user_id=eq.${currentUser.id}`
            }, () => {
                fetchUnread();
            })
            .subscribe();

        return () => supabase.removeChannel(channel);
    }, [currentUser]);

    useEffect(() => {
        setIsUserMenuOpen(false);
        setIsDrawerOpen(false);
        setDrawerMode('navigation');
    }, [location.pathname]);

    useEffect(() => {
        if (!isUserMenuOpen) return undefined;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setIsUserMenuOpen(false);
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isUserMenuOpen]);

    const navigationItems = [
        { title: '首頁', url: '/home', icon: Home, accent: colors.primary, selectedBg: '#eef4f9' },
        { title: '揪活動', url: '/events', icon: Calendar, accent: SECTION_THEME.events.accent, selectedBg: SECTION_THEME.events.tagBg },
        { title: '揪旅伴', url: '/travel', icon: Plane, accent: SECTION_THEME.travel.accent, selectedBg: SECTION_THEME.travel.tagBg },
        { title: '揪運動', url: '/sports', icon: Dumbbell, accent: SECTION_THEME.sports.accent, selectedBg: SECTION_THEME.sports.tagBg },
        { title: '我的', url: '/profile', icon: User, accent: colors.primary, selectedBg: '#eef4f9', isSelected: isMemberRoute },
    ];

    const renderNavItem = (item) => {
        const isSelected = item.isSelected ? item.isSelected(location.pathname) : location.pathname === item.url;
        const Icon = item.icon;

        const handleClick = (e) => {
            // Check if the item requires auth. Currently, only '我的' (User) really requires auth.
            // But we can add requiresAuth to navigationItems later. For now, hardcode check for '/profile' or similar if needed.
            // Actually, we can check if it's in isMemberRoute(item.url) or similar.
            if (item.url === '/profile' && !currentUser) {
                e.preventDefault();
                setIsLoginPromptOpen(true);
            }
        };

        return (
            <Link
                key={item.title}
                to={item.url}
                onClick={handleClick}
                className="flex flex-col items-center justify-center flex-1 py-1 transition-all duration-200 decoration-transparent"
                style={{ color: isSelected ? item.accent : '#64748b', fontWeight: isSelected ? '600' : '400' }}
            >
                <div
                    className="relative flex items-center justify-center px-4 py-1.5 rounded-2xl transition-colors duration-200"
                    style={{ background: isSelected ? item.selectedBg : 'transparent' }}
                >
                    <Icon
                        size={22}
                        fill={isSelected ? item.accent : 'none'}
                        strokeWidth={2}
                    />
                    {item.badge && (
                        <span style={{
                            position: 'absolute', top: '-2px', right: '-2px',
                            background: '#ef4444', color: 'white', fontSize: '10px',
                            fontWeight: '700', borderRadius: '20px', padding: '1px 5px',
                            minWidth: '16px', textAlign: 'center', lineHeight: '14px',
                        }}>
                            {item.badge > 99 ? '99+' : item.badge}
                        </span>
                    )}
                </div>
                <span className="text-[11px] mt-1">{item.title}</span>
            </Link>
        );
    };

    // 桌面版頂部導覽列連結（取代手機版底部 tab bar，多一個「揪好友」）
    const desktopNavItems = [
        { title: '首頁', url: '/home', icon: Home, accent: colors.primary },
        { title: '揪旅伴', url: '/travel', icon: Plane, accent: SECTION_THEME.travel.accent },
        { title: '揪運動', url: '/sports', icon: Dumbbell, accent: SECTION_THEME.sports.accent },
        { title: '揪活動', url: '/events', icon: Calendar, accent: SECTION_THEME.events.accent },
        { title: '揪好友', url: '/friends', icon: Users, accent: colors.primary },
    ];

    const renderDesktopNavItem = (item) => {
        const isSelected = location.pathname === item.url || location.pathname.startsWith(`${item.url}/`);
        const Icon = item.icon;

        const handleClick = (e) => {
            if (item.url === '/friends' && !currentUser) {
                e.preventDefault();
                setIsLoginPromptOpen(true);
            }
        };

        return (
            <Link
                key={item.title}
                to={item.url}
                onClick={handleClick}
                style={{
                    display: 'flex', alignItems: 'center', gap: '7px', padding: '9px 14px',
                    color: isSelected ? item.accent : '#475569', fontWeight: isSelected ? '700' : '600',
                    fontSize: '14.5px', textDecoration: 'none', borderRadius: '12px',
                    background: isSelected ? `${item.accent}14` : 'transparent', transition: 'all 0.15s'
                }}
            >
                <Icon size={17} strokeWidth={2.25} />
                {item.title}
            </Link>
        );
    };

    const levelInfo = getLevelInfo(currentUser?.profile?.total_points);

    const registerContextualDrawer = useCallback((config) => {
        setContextualDrawer(config);
    }, []);

    const unregisterContextualDrawer = useCallback((key) => {
        setContextualDrawer(current => current?.key === key ? null : current);
    }, []);

    const openDrawer = useCallback((mode = 'auto', trigger = null) => {
        drawerTriggerRef.current = trigger || document.activeElement;
        setDrawerMode(mode);
        setIsDrawerOpen(true);
    }, []);

    const closeDrawer = useCallback(() => {
        setIsDrawerOpen(false);
        const trigger = drawerTriggerRef.current;
        window.requestAnimationFrame(() => {
            if (trigger?.isConnected) trigger.focus();
        });
    }, []);

    const resolvedDrawerMode = drawerMode === 'auto'
        ? (isMemberRoute(location.pathname) ? 'member' : (contextualDrawer ? 'contextual' : 'navigation'))
        : drawerMode;

    const mobileDrawerValue = useMemo(() => ({
        isOpen: isDrawerOpen,
        mode: resolvedDrawerMode,
        contextualKey: contextualDrawer?.key || null,
        registerContextualDrawer,
        unregisterContextualDrawer,
        openDrawer,
        closeDrawer
    }), [closeDrawer, contextualDrawer?.key, isDrawerOpen, openDrawer, registerContextualDrawer, resolvedDrawerMode, unregisterContextualDrawer]);

    const handleLogout = () => {
        setIsUserMenuOpen(false);
        showConfirm('登出', '確定要登出嗎？', async () => {
            await supabase.auth.signOut();
            if (setSession) setSession(null);
            if (setCurrentUser) setCurrentUser(null);
            navigate('/');
        });
    };

    return (
        <MobileDrawerContext.Provider value={mobileDrawerValue}>
        <div className="bg-background" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <SidebarDrawer
                isOpen={isDrawerOpen}
                onClose={closeDrawer}
                mode={resolvedDrawerMode}
                contextualDrawer={contextualDrawer}
                isAdmin={currentUser?.profile?.role === 'admin'}
            />

            {/* Top Header — 手機版 */}
            {!isDesktop && (
            <header style={{
                position: 'fixed', top: 0, left: 0, right: 0, height: '60px',
                background: 'white', display: 'flex', alignItems: 'center', padding: '0 16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)', zIndex: 1000
            }}>
                {/* Left Side: Hamburger (Member) or JoinU Logo (Normal) */}
                <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                    {isMemberRoute(location.pathname) ? (
                        !needsOnboarding && currentUser && (
                            <button
                                type="button"
                                aria-label="開啟側邊選單"
                                aria-expanded={isDrawerOpen}
                                aria-controls="mobile-context-drawer"
                                onClick={(event) => openDrawer('auto', event.currentTarget)}
                                style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '36px', height: '36px', background: 'none', border: 'none', cursor: 'pointer', padding: '0', alignItems: 'flex-start' }}
                            >
                                <div style={{ width: '18px', height: '1.5px', background: '#374151', marginBottom: '4px', borderRadius: '1px' }} />
                                <div style={{ width: '18px', height: '1.5px', background: '#374151', marginBottom: '4px', borderRadius: '1px' }} />
                                <div style={{ width: '18px', height: '1.5px', background: '#374151', borderRadius: '1px' }} />
                            </button>
                        )
                    ) : (
                        <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}>
                            <img
                                src="/splash/logo_bar.png"
                                alt="JoinU"
                                style={{ display: 'block', width: '85px', height: 'auto' }}
                            />
                        </Link>
                    )}
                </div>

                {/* Center: Title (Member) or headerLeftAction (Location Selector) */}
                <div style={{ flex: 2, display: 'flex', justifyContent: 'center', textAlign: 'center' }}>
                    {isMemberRoute(location.pathname) ? (
                        <span style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>會員中心</span>
                    ) : (
                        headerLeftAction && headerLeftAction
                    )}
                </div>

                {/* Right Side: Bell + Hamburger (if not Member and no left action) */}
                <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px' }}>
                    {!isMemberRoute(location.pathname) && !needsOnboarding && !headerLeftAction && currentUser && (
                        <button
                            type="button"
                            aria-label="開啟側邊選單"
                            aria-expanded={isDrawerOpen}
                            aria-controls="mobile-context-drawer"
                            onClick={(event) => openDrawer('auto', event.currentTarget)}
                            style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '36px', height: '36px', background: 'none', border: 'none', cursor: 'pointer', padding: '0', alignItems: 'flex-end' }}
                        >
                            <div style={{ width: '18px', height: '1.5px', background: '#374151', marginBottom: '4px', borderRadius: '1px' }} />
                            <div style={{ width: '18px', height: '1.5px', background: '#374151', marginBottom: '4px', borderRadius: '1px' }} />
                            <div style={{ width: '18px', height: '1.5px', background: '#374151', borderRadius: '1px' }} />
                        </button>
                    )}
                    {!needsOnboarding && currentUser && (
                        <Link
                            to="/notifications"
                            style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '34px', height: '34px', borderRadius: '50%', background: location.pathname === '/notifications' ? '#eef4f9' : '#f3f4f6', color: location.pathname === '/notifications' ? colors.primary : '#4b5563', textDecoration: 'none', transition: 'all 0.2s' }}
                        >
                            <Bell size={18} strokeWidth={2} fill={location.pathname === '/notifications' ? colors.primary : 'none'} />
                            {unreadCount > 0 && (
                                <span style={{
                                    position: 'absolute', top: '1px', right: '1px',
                                    background: '#ef4444', width: '8px', height: '8px',
                                    borderRadius: '50%', border: '1.5px solid white'
                                }} aria-label={`${unreadCount} 則未讀通知`} />
                            )}
                        </Link>
                    )}
                    {!currentUser && (
                        <button
                            onClick={() => setIsLoginPromptOpen(true)}
                            className="bg-[#4e8bca] text-white px-4 py-1.5 rounded-full text-sm font-semibold shadow-sm"
                        >
                            登入/註冊
                        </button>
                    )}
                </div>
            </header>
            )}

            {/* Top Header — 桌面版：Logo + 導覽連結 + 通知 + 使用者選單，取代手機版頂欄與底部 tab bar */}
            {isDesktop && (
            <header style={{
                position: 'fixed', top: 0, left: 0, right: 0, height: '72px',
                background: 'white', borderBottom: '1px solid #eef2f6',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)', zIndex: 1000
            }}>
                <div style={{ maxWidth: '1360px', height: '100%', margin: '0 auto', padding: '0 32px', display: 'flex', alignItems: 'center', gap: '28px' }}>
                    <Link to="/home" style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }}>
                        <img
                            src="/splash/logo_bar.png"
                            alt="JoinU"
                            style={{ display: 'block', width: '109px', height: 'auto' }}
                        />
                    </Link>

                    {!needsOnboarding && (
                        <nav style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1 }}>
                            {desktopNavItems.map(renderDesktopNavItem)}
                        </nav>
                    )}

                    {!needsOnboarding && currentUser && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                            <Link
                                to="/notifications"
                                style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', borderRadius: '50%', background: location.pathname === '/notifications' ? '#eef4f9' : '#f3f4f6', color: location.pathname === '/notifications' ? colors.primary : '#4b5563', textDecoration: 'none', transition: 'all 0.2s' }}
                            >
                                <Bell size={19} strokeWidth={2} fill={location.pathname === '/notifications' ? colors.primary : 'none'} />
                                {unreadCount > 0 && (
                                    <span style={{
                                        position: 'absolute', top: '1px', right: '2px',
                                        background: '#ef4444', width: '9px', height: '9px',
                                        borderRadius: '50%', border: '1.5px solid white'
                                    }} aria-label={`${unreadCount} 則未讀通知`} />
                                )}
                            </Link>

                            <div style={{ position: 'relative' }}>
                                <button
                                    onClick={() => setIsUserMenuOpen(previous => !previous)}
                                    aria-label="開啟會員選單"
                                    aria-haspopup="menu"
                                    aria-expanded={isUserMenuOpen}
                                    style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '6px 10px 6px 6px', background: isUserMenuOpen ? '#f8fafc' : 'transparent', border: 'none', borderRadius: '999px', cursor: 'pointer' }}
                                >
                                    <AvatarDisplay avatarId={currentUser?.profile?.avatar} size={36} />
                                    <div style={{ textAlign: 'left' }}>
                                        <div style={{ fontSize: '13.5px', fontWeight: '750', color: '#1e293b', lineHeight: '1.3' }}>{currentUser?.profile?.name || '朋友'}</div>
                                        <div style={{ fontSize: '11px', color: '#94a3b8', lineHeight: '1.3' }}>LV.{levelInfo.level}・{levelInfo.title}</div>
                                    </div>
                                    <ChevronDown size={16} color="#94a3b8" style={{ transform: isUserMenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
                                </button>

                                {isUserMenuOpen && (
                                    <>
                                        <div onClick={() => setIsUserMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 1998 }} />
                                        <div role="menu" aria-label="會員快捷選單" style={{ position: 'absolute', top: 'calc(100% + 10px)', right: 0, width: '232px', background: '#ffffff', border: '1px solid #f1f5f9', borderRadius: '16px', boxShadow: '0 16px 36px rgba(15,23,42,0.14)', padding: '8px', zIndex: 1999 }}>
                                            {TOP_USER_MENU_SECTIONS.map((section, sectionIndex) => (
                                                <React.Fragment key={section.key}>
                                                    {sectionIndex > 0 && <div role="separator" style={{ height: '1px', background: '#f1f5f9', margin: '6px 4px' }} />}
                                                    {section.items.map(item => {
                                                        const isCurrent = location.pathname === item.path;
                                                        return (
                                                            <button
                                                                key={item.key}
                                                                type="button"
                                                                role="menuitem"
                                                                aria-current={isCurrent ? 'page' : undefined}
                                                                onClick={() => { navigate(item.path); setIsUserMenuOpen(false); }}
                                                                style={{ width: '100%', minHeight: '44px', display: 'flex', alignItems: 'center', gap: '11px', padding: '10px 12px', background: isCurrent ? '#eef4f9' : 'transparent', border: 'none', borderRadius: '10px', color: isCurrent ? '#376ba1' : '#374151', fontSize: '13.5px', fontWeight: isCurrent ? '750' : '650', cursor: 'pointer', textAlign: 'left' }}
                                                                onMouseEnter={e => { e.currentTarget.style.background = isCurrent ? '#e5eff7' : '#f8fafc'; }}
                                                                onMouseLeave={e => { e.currentTarget.style.background = isCurrent ? '#eef4f9' : 'transparent'; }}
                                                            >
                                                                <item.icon size={17} color={isCurrent ? '#4e8bca' : '#64748b'} aria-hidden="true" />{item.label}
                                                            </button>
                                                        );
                                                    })}
                                                </React.Fragment>
                                            ))}
                                            <div style={{ height: '1px', background: '#f1f5f9', margin: '6px 4px' }} />
                                            <button
                                                type="button"
                                                role="menuitem"
                                                onClick={handleLogout}
                                                style={{ width: '100%', minHeight: '44px', display: 'flex', alignItems: 'center', gap: '11px', padding: '10px 12px', background: 'transparent', border: 'none', borderRadius: '10px', color: '#dc2626', fontSize: '13.5px', fontWeight: '700', cursor: 'pointer', textAlign: 'left' }}
                                                onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; }}
                                                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                                            >
                                                <LogOut size={17} aria-hidden="true" />登出
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                    
                    {!needsOnboarding && !currentUser && (
                        <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                            <button
                                onClick={() => setIsLoginPromptOpen(true)}
                                className="bg-[#4e8bca] hover:bg-[#3d70a5] transition-colors text-white px-5 py-2 rounded-full text-sm font-semibold shadow-sm"
                            >
                                登入 / 註冊
                            </button>
                        </div>
                    )}
                </div>
            </header>
            )}

            <main className="w-full" style={{ flex: 1, paddingBottom: isDesktop ? '0' : '80px', paddingTop: isDesktop ? '72px' : '60px', paddingLeft: '0', paddingRight: '0', background: '#ffffff' }}>
                <Outlet />
            </main>
            {!isDesktop && !needsOnboarding && (
                <nav style={{
                    position: 'fixed', bottom: 0, left: 0, right: 0, height: '70px',
                    background: colors.white, borderTop: '1px solid #e5e7eb',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-around',
                    padding: '0 10px', boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.05)', zIndex: 1000,
                }}>
                    {navigationItems.map(renderNavItem)}
                </nav>
            )}

            <LoginPromptModal isOpen={isLoginPromptOpen} onClose={() => setIsLoginPromptOpen(false)} />
        </div>
        </MobileDrawerContext.Provider>
    );
}
